// ============================================================
// AdminSignupPage.jsx
//
// Admin dashboard for reviewing registered users and granting
// them access to the Course Hub after payment confirmation.
//
// FLOW:
//   1. Fetches all users from Firestore "users" collection
//   2. Admin reviews their profile details
//   3. Admin clicks "Grant Course Access"
//   4. Firestore updates courseAccess: true on their document
//   5. User logs in → SignIn.jsx routes them to /course-hub
//
// No new Firebase Auth accounts are created here.
// Users already have accounts from self-signup.
// ============================================================

import React, { useState, useEffect } from "react";
import {
    Card, Typography, Button, Grid, TextField,
    Snackbar, Alert, MenuItem, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, InputAdornment, Chip, Box, Divider,
} from "@mui/material";

// ── Icons ────────────────────────────────────────────────────
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

// ── Firebase ─────────────────────────────────────────────────
import { auth, db } from "../firebase";
import {
    collection, getDocs, doc,
    updateDoc, addDoc, serverTimestamp,
} from "firebase/firestore";

// ── Utilities ────────────────────────────────────────────────
import { checkAdmin } from "../utils/AuthHelpers";

// ============================================================
//  HELPER FUNCTIONS
// ============================================================

/**
 * writeAuditLog
 * Records an admin action (granting or revoking course access) in
 * Firestore's auditLogs collection, so every access change has an
 * accountability trail.
 *
 * `action` is passed in explicitly by the caller (e.g.
 * "grant_course_access" or "revoke_course_access") rather than being
 * hardcoded here — otherwise every entry would read as a "grant" even
 * when it was really a revoke, which used to be a real bug in this file.
 */
const writeAuditLog = async (action, adminUid, adminEmail, targetUser, targetEmail) => {
    await addDoc(collection(db, "auditLogs"), {
        action,
        performedBy: adminUid,
        adminEmail,
        targetUser,
        targetEmail,
        timestamp: serverTimestamp(),
    });
};

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function AdminSignupPage() {

    // ── Admin Verification ───────────────────────────────────
    const [isAdmin, setIsAdmin] = useState(null);

    // ── Data State ───────────────────────────────────────────
    const [users, setUsers] = useState([]);           // All users from Firestore
    const [selectedUser, setSelectedUser] = useState(null); // Currently selected user
    const [searchQuery, setSearchQuery] = useState("");     // Search filter

    // ── UI State ─────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    // ============================================================
    //  EFFECTS
    // ============================================================

    // Verify admin role on mount
    useEffect(() => {
        const verifyRole = async () => {
            const result = await checkAdmin();
            setIsAdmin(result);
        };
        verifyRole();
    }, []);

    // Fetch all users once admin is verified
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const snapshot = await getDocs(collection(db, "users"));
                const data = snapshot.docs.map((doc) => ({
                    id: doc.id,   // This is the Firebase Auth UID
                    ...doc.data(),
                }));

                // Only show regular users, not admins
                const regularUsers = data.filter((u) => u.role !== "admin");
                setUsers(regularUsers);

            } catch (err) {
                console.error("Error fetching users:", err);
                setError("Failed to load users. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        if (isAdmin) fetchUsers();
    }, [isAdmin]);

    // ============================================================
    //  HANDLERS
    // ============================================================

    /**
     * handleSelectUser
     * Sets the selected user from the dropdown.
     */
    const handleSelectUser = (e) => {
        const user = users.find((u) => u.id === e.target.value);
        setSelectedUser(user);
    };

    /**
     * handleGrantAccess
     * Called when admin clicks "Confirm & Grant Access" in the dialog.
     *
     * Updates the user's Firestore document:
     *   courseAccess: true  → unlocks /course-hub on next login
     *   accessGrantedAt     → timestamp for records
     *   accessGrantedBy     → admin email for accountability
     *
     * Then writes an audit log entry.
     */
    const handleGrantAccess = async () => {
        setConfirmOpen(false);
        setSaving(true);

        try {
            // Step 1: Update the user's Firestore document
            await updateDoc(doc(db, "users", selectedUser.id), {
                courseAccess:    true,
                accessGrantedAt: serverTimestamp(),
                accessGrantedBy: auth.currentUser.email,
            });

            // Step 2: Write audit log
            await writeAuditLog(
                "grant_course_access",
                auth.currentUser.uid,
                auth.currentUser.email,
                selectedUser.username || selectedUser.email,
                selectedUser.email,
            );

            // Step 3: Update local state so UI reflects the change immediately
            setUsers((prev) =>
                prev.map((u) =>
                    u.id === selectedUser.id
                        ? { ...u, courseAccess: true }
                        : u
                )
            );

            // Step 4: Reset and show success
            setSelectedUser(null);
            setSuccess(true);

        } catch (err) {
            console.error("Error granting access:", err);
            if (err.code === "auth/id-token-expired") {
                setError("Your session has expired. Please log in again.");
            } else {
                setError(err.message || "Failed to grant access. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    /**
     * handleRevokeAccess
     * Allows admin to revoke course access if needed
     * (e.g. payment reversed, account suspended).
     *
     * Takes the full user object (not just the id) so the audit log can
     * record the correct email — passing only an id previously caused
     * the wrong value to end up in the log's email field.
     */
    const handleRevokeAccess = async (user) => {
        try {
            await updateDoc(doc(db, "users", user.id), {
                courseAccess: false,
                accessRevokedAt: serverTimestamp(),
                accessRevokedBy: auth.currentUser.email,
            });

            // Update local state immediately
            setUsers((prev) =>
                prev.map((u) =>
                    u.id === user.id ? { ...u, courseAccess: false } : u
                )
            );

            await writeAuditLog(
                "revoke_course_access",
                auth.currentUser.uid,
                auth.currentUser.email,
                user.username || user.email,
                user.email,
            );

        } catch (err) {
            setError("Failed to revoke access. Please try again.");
        }
    };

    // ============================================================
    //  DERIVED DATA
    // ============================================================

    /**
     * filteredUsers
     * Filters users by search query across name, email, username.
     * Only shows users who do NOT yet have course access
     * in the dropdown selection.
     */
    const pendingUsers = users.filter((u) => !u.courseAccess);

    const filteredUsers = pendingUsers.filter((u) => {
        const q = searchQuery.toLowerCase();
        return (
            u.firstname?.toLowerCase().includes(q) ||
            u.lastname?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q) ||
            u.username?.toLowerCase().includes(q)
        );
    });

    // Users who already have access — shown in the access list below
    const grantedUsers = users.filter((u) => u.courseAccess);

    // ============================================================
    //  EARLY RETURNS (Guards)
    // ============================================================

    if (isAdmin === null) {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
                <CircularProgress />
            </Grid>
        );
    }

    if (!isAdmin) {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
                <Typography variant="h6" color="error">
                    Access Denied: Admins Only
                </Typography>
            </Grid>
        );
    }

    // ============================================================
    //  MAIN RENDER
    // ============================================================

    return (
        <Grid
            container justifyContent="center"
            sx={{ minHeight: "100vh", backgroundColor: "#f9fafc", padding: "40px 20px" }}
        >
            <Box sx={{ width: "100%", maxWidth: 750 }}>

                {/* ── Page Header ── */}
                <Typography variant="h4" fontWeight={800} color="#100720" gutterBottom
                            sx={{ fontFamily: "'Georgia', serif" }}>
                    Course Access Manager
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>
                    Review registered users and grant course access after payment confirmation.
                </Typography>

                {/* ── Stats Row ── */}
                <Grid container spacing={2} mb={4}>
                    {[
                        { label: "Total Users",     value: users.length,        color: "#09122C" },
                        { label: "Pending Access",  value: pendingUsers.length, color: "#f59e0b" },
                        { label: "Access Granted",  value: grantedUsers.length, color: "#10b981" },
                    ].map((stat) => (
                        <Grid item xs={12} md={4} key={stat.label}>
                            <Card sx={{
                                p: 3, borderRadius: "12px",
                                borderLeft: `5px solid ${stat.color}`,
                                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                            }}>
                                <Typography variant="h4" fontWeight={800} color={stat.color}>
                                    {stat.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {stat.label}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* ── Grant Access Card ── */}
                <Card sx={{
                    p: 4, borderRadius: "16px",
                    borderLeft: "6px solid goldenrod",
                    boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
                    mb: 4,
                }}>
                    <Typography variant="h6" fontWeight={700} color="#100720" gutterBottom>
                        Grant Course Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Select a registered user and grant them access to the Course Hub.
                        Ensure payment has been confirmed before proceeding.
                    </Typography>

                    {loading ? (
                        <Grid container justifyContent="center" sx={{ py: 4 }}>
                            <CircularProgress />
                        </Grid>
                    ) : (
                        <>
                            {/* Search */}
                            <TextField
                                fullWidth label="Search Users"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                margin="normal"
                                placeholder="Search by name, email, or username..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* User Dropdown — only pending users */}
                            <TextField
                                select fullWidth
                                label="Select User (Pending Access)"
                                value={selectedUser?.id || ""}
                                onChange={handleSelectUser}
                                margin="normal"
                                disabled={filteredUsers.length === 0}
                                helperText={
                                    filteredUsers.length === 0
                                        ? "No pending users found"
                                        : `${filteredUsers.length} user(s) pending access`
                                }
                            >
                                {filteredUsers.length === 0 ? (
                                    <MenuItem disabled>No pending users</MenuItem>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.firstname
                                                ? `${user.firstname} ${user.lastname}`
                                                : user.username || user.email
                                            } — {user.plan || "No plan"}
                                        </MenuItem>
                                    ))
                                )}
                            </TextField>

                            {/* ── Selected User Details ── */}
                            {selectedUser && (
                                <Box
                                    sx={{
                                        mt: 3, p: 3, borderRadius: "12px",
                                        backgroundColor: "#f9fafc",
                                        border: "1px solid rgba(0,0,0,0.08)",
                                    }}
                                >
                                    <Typography fontWeight={700} color="#09122C" mb={2}>
                                        User Details
                                    </Typography>

                                    <Grid container spacing={1.5}>
                                        {[
                                            { label: "Full Name",    value: `${selectedUser.firstname || ""} ${selectedUser.lastname || ""}`.trim() || "—" },
                                            { label: "Username",     value: selectedUser.username    || "—" },
                                            { label: "Email",        value: selectedUser.email       || "—" },
                                            { label: "Phone",        value: selectedUser.phone       || "—" },
                                            { label: "Nationality",  value: selectedUser.nationality || "—" },
                                            { label: "Plan",         value: selectedUser.plan        || "—" },
                                        ].map((field) => (
                                            <Grid item xs={12} sm={6} key={field.label}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {field.label}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600} color="#09122C">
                                                    {field.value}
                                                </Typography>
                                            </Grid>
                                        ))}

                                        {/* LinkedIn */}
                                        {selectedUser.linkedin && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary">
                                                    LinkedIn
                                                </Typography>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <LinkedInIcon sx={{ color: "#0077b5", fontSize: 18 }} />
                                                    <Typography
                                                        variant="body2" fontWeight={600}
                                                        component="a"
                                                        href={selectedUser.linkedin}
                                                        target="_blank"
                                                        sx={{ color: "#0077b5" }}
                                                    >
                                                        View Profile
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}

                                        {/* Profile status */}
                                        <Grid item xs={12} mt={1}>
                                            <Chip
                                                icon={selectedUser.profileComplete
                                                    ? <CheckCircleIcon />
                                                    : <WarningAmberIcon />
                                                }
                                                label={selectedUser.profileComplete
                                                    ? "Profile Complete"
                                                    : "Profile Incomplete"
                                                }
                                                color={selectedUser.profileComplete ? "success" : "warning"}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Grant Access Button */}
                                    <Button
                                        fullWidth variant="contained"
                                        startIcon={saving
                                            ? <CircularProgress size={18} color="inherit" />
                                            : <LockOpenIcon />
                                        }
                                        disabled={saving}
                                        onClick={() => setConfirmOpen(true)}
                                        sx={{
                                            mt: 3,
                                            backgroundColor: "#10b981",
                                            color: "white",
                                            fontWeight: "bold",
                                            py: 1.3,
                                            "&:hover": { backgroundColor: "#059669" },
                                        }}
                                    >
                                        Grant Course Access
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Card>

                {/* ── Users With Access ── */}
                {grantedUsers.length > 0 && (
                    <Card sx={{
                        p: 4, borderRadius: "16px",
                        borderLeft: "6px solid #10b981",
                        boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
                    }}>
                        <Typography variant="h6" fontWeight={700} color="#100720" gutterBottom>
                            Users With Course Access
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            These users can currently access the Course Hub.
                            You can revoke access if needed.
                        </Typography>

                        <Box display="flex" flexDirection="column" gap={1.5}>
                            {grantedUsers.map((user) => (
                                <Box
                                    key={user.id}
                                    display="flex" alignItems="center"
                                    justifyContent="space-between" flexWrap="wrap" gap={2}
                                    sx={{
                                        p: 2, borderRadius: "10px",
                                        backgroundColor: "#f0fdf4",
                                        border: "1px solid rgba(16,185,129,0.2)",
                                    }}
                                >
                                    <Box>
                                        <Typography fontWeight={700} color="#09122C">
                                            {user.firstname
                                                ? `${user.firstname} ${user.lastname}`
                                                : user.username || user.email
                                            }
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {user.email} · {user.plan || "No plan"}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            icon={<CheckCircleIcon />}
                                            label="Access Granted"
                                            color="success"
                                            size="small"
                                        />
                                        {/* Revoke button — passes the whole user object so the
                                            audit log can record the correct email */}
                                        <Button
                                            size="small" variant="outlined"
                                            color="error"
                                            onClick={() => handleRevokeAccess(user)}
                                            sx={{ fontSize: "0.7rem" }}
                                        >
                                            Revoke
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Card>
                )}
            </Box>

            {/* ── Confirmation Dialog ── */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    Confirm Course Access Grant
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to grant full Course Hub access to:
                        <br /><br />
                        <strong>Name:</strong>{" "}
                        {selectedUser?.firstname
                            ? `${selectedUser.firstname} ${selectedUser.lastname}`
                            : selectedUser?.username || selectedUser?.email
                        }<br />
                        <strong>Email:</strong> {selectedUser?.email}<br />
                        <strong>Plan:</strong> {selectedUser?.plan || "—"}
                        <br /><br />
                        Please confirm that payment has been received before proceeding.
                        This action will be logged.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGrantAccess}
                        variant="contained"
                        sx={{ backgroundColor: "#10b981", color: "white" }}
                    >
                        Confirm & Grant Access
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Success Snackbar ── */}
            <Snackbar
                open={success} autoHideDuration={4000}
                onClose={() => setSuccess(false)}
            >
                <Alert severity="success" onClose={() => setSuccess(false)}
                       sx={{ width: "100%", backgroundColor: "#10b981", color: "white" }}>
                    Course access granted successfully! The user can now access the Course Hub.
                </Alert>
            </Snackbar>

            {/* ── Error Snackbar ── */}
            <Snackbar
                open={!!error} autoHideDuration={5000}
                onClose={() => setError("")}
            >
                <Alert severity="error" onClose={() => setError("")}
                       sx={{ width: "100%", backgroundColor: "#d32f2f", color: "white" }}>
                    {error}
                </Alert>
            </Snackbar>

        </Grid>
    );
}
